import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Tabs,
  Button,
  InputNumber,
  Space,
  Tag,
  Alert,
  Breadcrumb,
  Spin,
  message,
} from "antd";
import {
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import apiService from "../../services/apiService";

function Settings({ title }) {
  const [bidsData, setBidsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingRow, setEditingRow] = useState(null); // { category, index }
  const [editingValue, setEditingValue] = useState(null);
  const [saving, setSaving] = useState(false);

  // Active tab & Pagination state
  const [activeTab, setActiveTab] = useState("clients");
  const [paginationState, setPaginationState] = useState({
    current: 1,
    pageSize: 10,
  });

  // Handle tab change: reset to page 1 & cancel any active inline edit
  const handleTabChange = (key) => {
    setActiveTab(key);
    setPaginationState((prev) => ({ ...prev, current: 1 }));
    handleCancel();
  };

  // Fetch bids weights data from API
  const fetchBidsData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.get("masterSettings/view/?key=bids");
      if (response.status === 200 && response.data?.value) {
        setBidsData(response.data.value);
      }
    } catch (error) {
      console.error("Failed to fetch bids data:", error);
      message.error(
        error.response?.data?.message || "Failed to fetch bids weights data."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBidsData();
  }, [fetchBidsData]);

  // Start inline editing for a specific row
  const handleEdit = (category, index, currentPercentage) => {
    setEditingRow({ category, index });
    setEditingValue(currentPercentage ?? 0);
  };

  // Cancel inline editing
  const handleCancel = () => {
    setEditingRow(null);
    setEditingValue(null);
  };

  // Save the modified weight percentage
  const handleSave = async (category, index) => {
    if (
      editingValue === null ||
      editingValue === undefined ||
      isNaN(editingValue)
    ) {
      message.error("Please enter a valid percentage number.");
      return;
    }

    const numPct = Number(editingValue);
    if (numPct < 0 || numPct > 100) {
      message.error("Weight percentage must be between 0% and 100%.");
      return;
    }

    setSaving(true);
    try {
      const response = await apiService.post(
        "masterSettings/updateBidsWeights",
        {
          category,
          index,
          percentage: numPct,
        }
      );

      if (response.status === 200) {
        message.success(
          response.data?.message || "Weight updated successfully!"
        );

        // Update local state immediately for instant feedback
        setBidsData((prev) => {
          if (!prev || !prev[category]) return prev;
          const updatedCategory = [...prev[category]];
          if (updatedCategory[index]) {
            updatedCategory[index] = {
              ...updatedCategory[index],
              percentage: numPct,
            };
          }
          return {
            ...prev,
            [category]: updatedCategory,
          };
        });

        setEditingRow(null);
        setEditingValue(null);
      } else {
        message.error(response.data?.message || "Failed to update weight.");
      }
    } catch (error) {
      console.error("Error saving weight:", error);
      message.error(
        error.response?.data?.message ||
          "Failed to update weight. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // Helper description for each weight category
  const categoryDescriptions = {
    clients:
      "Client weights contribute to the Smart Bid Score when the opportunity's general contractor/client matches a configured company name.",
    states:
      "State weights contribute when the opportunity project location matches the designated state abbreviation.",
    distance:
      "Distance weights evaluate the driving distance (miles) from your base location to the project site.",
    tradeNames:
      "Trade name weights evaluate whether the bid opportunity matches standard specialty trade scopes (e.g. Toilet Partitions).",
    projectNames:
      "Project name weights check if the opportunity project name contains high-value keywords (e.g. School, Hospital, Data Center).",
    projectTimeline:
      "Project timeline weights reward opportunities based on days remaining until the bid deadline.",
    projectSize:
      "Project size weights score opportunities based on their estimated contract dollar value.",
  };

  // Formatter for range display
  const formatRangeValue = (val, category, isMax = false) => {
    if (val === null || val === undefined) {
      return isMax ? <Tag color="default">No Max (Above)</Tag> : "0";
    }
    if (category === "distance") {
      return `${val} miles`;
    }
    if (category === "projectTimeline") {
      return `${val} days`;
    }
    if (category === "projectSize") {
      return `$${Number(val).toLocaleString()}`;
    }
    return val;
  };

  // Render editable table for any category
  const renderTable = (data, category) => {
    if (!data) return null;

    // Attach original index to each row to support accurate editing across paginated pages
    const tableData = data.map((item, originalIndex) => ({
      ...item,
      _originalIndex: originalIndex,
    }));

    const isRangeType =
      category === "distance" ||
      category === "projectTimeline" ||
      category === "projectSize";

    const columns = [
      {
        title: "Sl No.",
        key: "index",
        width: 70,
        render: (_text, record) => record._originalIndex + 1,
      },
    ];

    if (isRangeType) {
      columns.push(
        {
          title: "Min Range",
          dataIndex: "min",
          key: "min",
          render: (val) => formatRangeValue(val, category, false),
        },
        {
          title: "Max Range",
          dataIndex: "max",
          key: "max",
          render: (val) => formatRangeValue(val, category, true),
        }
      );
    } else {
      columns.push({
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (name) => <span className="font-weight-bold">{name}</span>,
      });
    }

    // Editable Percentage Column
    columns.push({
      title: "Weight (%)",
      dataIndex: "percentage",
      key: "percentage",
      width: 170,
      render: (val, record) => {
        const isEditing =
          editingRow?.category === category &&
          editingRow?.index === record._originalIndex;

        if (isEditing) {
          return (
            <InputNumber
              min={0}
              max={100}
              step={1}
              value={editingValue}
              onChange={(v) => setEditingValue(v)}
              onPressEnter={() => handleSave(category, record._originalIndex)}
              autoFocus
              style={{ width: "90px" }}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace("%", "")}
            />
          );
        }

        return (
          <Tag color="blue" style={{ fontSize: "13px", padding: "2px 8px" }}>
            {val ?? 0}%
          </Tag>
        );
      },
    });

    // Action Column (Edit / Save / Cancel)
    columns.push({
      title: "Action",
      key: "action",
      width: 150,
      render: (_text, record) => {
        const isEditing =
          editingRow?.category === category &&
          editingRow?.index === record._originalIndex;

        if (isEditing) {
          return (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                loading={saving}
                onClick={() => handleSave(category, record._originalIndex)}
              >
                Save
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                disabled={saving}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Space>
          );
        }

        return (
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() =>
              handleEdit(category, record._originalIndex, record.percentage)
            }
          >
            Edit
          </Button>
        );
      },
    });

    return (
      <div>
        {categoryDescriptions[category] && (
          <Alert
            message={categoryDescriptions[category]}
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}
        <Table
          loading={loading}
          rowKey={(record) => `${category}-${record._originalIndex}`}
          dataSource={tableData}
          columns={columns}
          pagination={{
            current: paginationState.current,
            pageSize: paginationState.pageSize,
            pageSizeOptions: ["10", "20", "25", "50", "100"],
            showSizeChanger: true,
            showTotal: (total, range) =>
              `Showing ${range[0]} to ${range[1]} of ${total} entries`,
            onChange: (page, pageSize) => {
              setPaginationState({
                current: page,
                pageSize: pageSize,
              });
            },
          }}
          bordered
          size="middle"
        />
      </div>
    );
  };

  const tabItems = [
    {
      key: "clients",
      label: `Clients (${bidsData?.clients?.length || 0})`,
      children: renderTable(bidsData?.clients, "clients"),
    },
    {
      key: "states",
      label: `States (${bidsData?.states?.length || 0})`,
      children: renderTable(bidsData?.states, "states"),
    },
    {
      key: "distance",
      label: `Distance (${bidsData?.distance?.length || 0})`,
      children: renderTable(bidsData?.distance, "distance"),
    },
    {
      key: "tradeNames",
      label: `Trade Names (${bidsData?.tradeNames?.length || 0})`,
      children: renderTable(bidsData?.tradeNames, "tradeNames"),
    },
    {
      key: "projectNames",
      label: `Project Names (${bidsData?.projectNames?.length || 0})`,
      children: renderTable(bidsData?.projectNames, "projectNames"),
    },
    {
      key: "projectTimeline",
      label: `Timeline (${bidsData?.projectTimeline?.length || 0})`,
      children: renderTable(bidsData?.projectTimeline, "projectTimeline"),
    },
    {
      key: "projectSize",
      label: `Project Size (${bidsData?.projectSize?.length || 0})`,
      children: renderTable(bidsData?.projectSize, "projectSize"),
    },
  ];

  return (
    <div className="container-fluid">
      <Breadcrumb
        style={{ marginBottom: "16px" }}
        items={[
          { title: <Link to="/dashboard">Home</Link> },
          { title: "Bids" },
          { title: title || "Bids Setting" },
        ]}
      />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 text-gray-800 mb-1">
            {title || "Smart Bid Score (SBS) Settings"}
          </h1>
          <p className="text-muted mb-0 small">
            Configure scoring weight percentages used by the Sales team to prioritize opportunities.
          </p>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchBidsData}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      <Spin spinning={loading}>
        <Card>
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            type="card"
            items={tabItems}
          />
        </Card>
      </Spin>
    </div>
  );
}

export default Settings;
